import React, { forwardRef } from 'react';
import { DivideIcon as LucideIcon } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  variant?: 'default' | 'filled';
}

/**
 * Design System Input Component
 * 
 * Features:
 * - WCAG AA compliant contrast ratios
 * - Clear focus states with ring indicators
 * - Error states with accessible color coding
 * - Optional icons with proper spacing
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  icon: Icon,
  iconPosition = 'left',
  variant = 'default',
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseInputStyles = `
    w-full px-4 py-3 text-sm text-text-primary
    rounded-lg transition-all duration-200 ease-out
    placeholder:text-text-muted
    focus:outline-none focus:ring-2 focus:ring-offset-1
    disabled:cursor-not-allowed disabled:opacity-50
  `;

  const variantStyles = {
    default: `
      bg-seasalt border border-border-medium
      hover:border-border-dark
      focus:border-eerie-black focus:ring-slate-gray/20
    `,
    filled: `
      bg-surface-secondary border border-transparent
      hover:bg-platinum
      focus:bg-seasalt focus:border-border-dark focus:ring-slate-gray/20
    `,
  };

  const errorStyles = error ? `
    border-red-300 bg-red-50
    focus:border-red-500 focus:ring-red-200
  ` : '';

  const iconPadding = Icon ? (iconPosition === 'left' ? 'pl-11' : 'pr-11') : '';

  const inputClassName = `
    ${baseInputStyles}
    ${variantStyles[variant]}
    ${errorStyles}
    ${iconPadding}
    ${className}
  `.trim();

  return (
    <div className="space-y-2">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-text-primary"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className={`
            absolute inset-y-0 ${iconPosition === 'left' ? 'left-0' : 'right-0'}
            flex items-center ${iconPosition === 'left' ? 'pl-3' : 'pr-3'}
            pointer-events-none
          `}>
            <Icon className={`w-5 h-5 ${error ? 'text-red-400' : 'text-text-tertiary'}`} />
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClassName}
          {...props}
        />
      </div>
      
      {error && (
        <p className="text-sm text-red-600 animate-fade-in">
          {error}
        </p>
      )}
      
      {hint && !error && (
        <p className="text-sm text-text-tertiary">
          {hint}
        </p>
      )}
    </div>
  );
});